import axios from "axios";
import { jwtDecode } from "jwt-decode";
import { getAgenteContabilidadeClientContext } from "../../../utils/agenteContabilidadeAccess";

/**
 * Camada de API do modulo Agente de Contabilidade.
 * Base dos endpoints: /api/produto/contabilidade.
 */

const baseURL = process.env.REACT_APP_API_BASE_URL;
const CACHE_TTL_MS = 60 * 60 * 1000;
const SESSION_CACHE_PREFIX = "agente-contabilidade-cache";
const SESSION_CACHE_DOMAINS = new Set(["sienge-cost-centers"]);
const responseCache = new Map();
const pendingRequests = new Map();
let activeSessionKey = "";

function getStoredToken() {
  return (
    localStorage.getItem("jwtToken") ||
    localStorage.getItem("authToken") ||
    sessionStorage.getItem("token") ||
    ""
  );
}

function normalizeToken(token) {
  return token?.startsWith("Bearer ") ? token.slice(7) : token;
}

function createAuthExpiredError(message = "Sessao expirada. Faca login novamente.") {
  const error = new Error(message);
  error.isAuthExpired = true;
  return error;
}

function notifyAuthExpired() {
  clearContabilidadeApiCache();
  localStorage.removeItem("jwtToken");
  localStorage.removeItem("authToken");
  sessionStorage.removeItem("token");
  window.dispatchEvent(new Event("tokenExpired"));
}

function getValidAuthToken() {
  const token = normalizeToken(getStoredToken());

  if (!token) {
    notifyAuthExpired();
    throw createAuthExpiredError("Token de autenticacao nao encontrado.");
  }

  try {
    const decoded = jwtDecode(token);
    const currentTime = Date.now() / 1000;

    if (decoded?.exp && decoded.exp < currentTime) {
      notifyAuthExpired();
      throw createAuthExpiredError();
    }

    return token;
  } catch (error) {
    if (error?.isAuthExpired) {
      throw error;
    }

    notifyAuthExpired();
    throw createAuthExpiredError("Token de autenticacao invalido.");
  }
}

function isAuthError(error) {
  const message = String(error?.response?.data?.message || error?.message || "").toLowerCase();

  return Boolean(
    error?.isAuthExpired ||
      error?.response?.status === 401 ||
      message.includes("token") ||
      message.includes("expir") ||
      message.includes("sess")
  );
}

function handleRequestError(error) {
  if (isAuthError(error)) {
    notifyAuthExpired();
  }

  throw error;
}

const TECHNICAL_ERROR_PATTERN =
  /(sql|hibernate|jdbc|constraint|duplicate key|insert into|update\s+\w+|delete from|select\s+.+\s+from|stack trace|exception|dbo\.|ux_|pk_|fk_)/i;

export function getContabilidadeApiErrorMessage(
  error,
  fallback = "Nao foi possivel concluir a operacao agora. Tente novamente."
) {
  const payload = error?.response?.data;
  const candidate =
    (typeof payload?.mensagem === "string" && payload.mensagem) ||
    (typeof payload?.message === "string" && payload.message) ||
    "";
  const normalized = candidate.trim();

  if (
    !normalized ||
    normalized.length > 240 ||
    TECHNICAL_ERROR_PATTERN.test(normalized)
  ) {
    return fallback;
  }

  return normalized;
}

export function getContabilidadeApiErrorDetails(error) {
  const payload = error?.response?.data;
  if (!Array.isArray(payload?.erros)) {
    return [];
  }

  return payload.erros
    .filter((item) => typeof item === "string")
    .map((item) => item.trim())
    .filter((item) => item && item.length <= 220 && !TECHNICAL_ERROR_PATTERN.test(item));
}

export function logContabilidadeApiError(context, error) {
  if (process.env.NODE_ENV !== "development") {
    return;
  }

  const payload = error?.response?.data;
  console.warn("Agente de Contabilidade:", {
    context,
    status: error?.response?.status || null,
    codigo: payload?.codigo || null,
    correlationId: payload?.correlationId || null,
  });
}

function getSessionKey() {
  const token = normalizeToken(getStoredToken());
  const context = getAgenteContabilidadeClientContext();
  return `${context.idCliente || "anon"}|${token}`;
}

function ensureCurrentSession() {
  const nextSessionKey = getSessionKey();

  if (activeSessionKey && activeSessionKey !== nextSessionKey) {
    clearContabilidadeApiCache();
  }

  activeSessionKey = nextSessionKey;
  return nextSessionKey;
}

function normalizeParams(params = {}) {
  return Object.entries(params || {})
    .filter(([, value]) => value !== undefined && value !== null && value !== "")
    .sort(([leftKey], [rightKey]) => leftKey.localeCompare(rightKey))
    .map(([key, value]) => `${key}=${String(value)}`)
    .join("&");
}

function buildCacheKey(domain, params = {}) {
  const sessionKey = ensureCurrentSession();
  const normalizedParams = normalizeParams(params);
  return `${sessionKey}|endpoint=${domain}|${normalizedParams}`;
}

function getSessionStorageCacheKey(cacheKey) {
  return `${SESSION_CACHE_PREFIX}|${cacheKey}`;
}

function readSessionCache(cacheKey) {
  if (typeof window === "undefined" || !window.sessionStorage) {
    return null;
  }

  try {
    const rawValue = window.sessionStorage.getItem(getSessionStorageCacheKey(cacheKey));
    return rawValue ? JSON.parse(rawValue) : null;
  } catch {
    window.sessionStorage.removeItem(getSessionStorageCacheKey(cacheKey));
    return null;
  }
}

function writeSessionCache(cacheKey, data) {
  if (typeof window === "undefined" || !window.sessionStorage) {
    return;
  }

  try {
    window.sessionStorage.setItem(getSessionStorageCacheKey(cacheKey), JSON.stringify(data));
  } catch {
    // Cache de sessao e apenas um acelerador; falhas nao devem afetar o fluxo.
  }
}

function clearSessionCache(domains = []) {
  if (typeof window === "undefined" || !window.sessionStorage) {
    return;
  }

  const domainList = Array.isArray(domains) ? domains : domains ? [domains] : [];
  const matchDomains = domainList.map((domain) => `|endpoint=${domain}|`);

  for (let index = window.sessionStorage.length - 1; index >= 0; index -= 1) {
    const key = window.sessionStorage.key(index);
    if (!key || !key.startsWith(`${SESSION_CACHE_PREFIX}|`)) {
      continue;
    }
    if (!matchDomains.length || matchDomains.some((domainKey) => key.includes(domainKey))) {
      window.sessionStorage.removeItem(key);
    }
  }
}

export function clearContabilidadeApiCache(domains) {
  const domainList = Array.isArray(domains) ? domains : domains ? [domains] : [];

  if (!domainList.length) {
    responseCache.clear();
    pendingRequests.clear();
    clearSessionCache();
    return;
  }

  const matchDomains = domainList.map((domain) => `|endpoint=${domain}|`);
  clearSessionCache(domainList);

  Array.from(responseCache.keys()).forEach((key) => {
    if (matchDomains.some((domainKey) => key.includes(domainKey))) {
      responseCache.delete(key);
    }
  });

  Array.from(pendingRequests.keys()).forEach((key) => {
    if (matchDomains.some((domainKey) => key.includes(domainKey))) {
      pendingRequests.delete(key);
    }
  });
}

function authHeaders() {
  ensureCurrentSession();
  const token = getValidAuthToken();
  return {
    Authorization: `Bearer ${token}`,
    "Content-Type": "application/json",
  };
}

function authMultipartHeaders() {
  ensureCurrentSession();
  const token = getValidAuthToken();
  return {
    Authorization: `Bearer ${token}`,
  };
}

function endpoint(path) {
  return `${baseURL}/produto/contabilidade${path}`;
}

async function cachedGet(domain, path, params = {}, headers = authMultipartHeaders()) {
  const cacheKey = buildCacheKey(domain, params);
  const shouldUseSessionCache = SESSION_CACHE_DOMAINS.has(domain);
  const sessionCachedResponse = shouldUseSessionCache ? readSessionCache(cacheKey) : null;
  if (sessionCachedResponse) {
    responseCache.set(cacheKey, {
      data: sessionCachedResponse,
      expiresAt: Date.now() + CACHE_TTL_MS,
    });
    return sessionCachedResponse;
  }

  const cachedResponse = responseCache.get(cacheKey);
  const now = Date.now();

  if (cachedResponse && cachedResponse.expiresAt > now) {
    return cachedResponse.data;
  }

  if (pendingRequests.has(cacheKey)) {
    return pendingRequests.get(cacheKey);
  }

  const request = axios
    .get(endpoint(path), {
      params,
      headers,
    })
    .then(({ data }) => {
      responseCache.set(cacheKey, {
        data,
        expiresAt: Date.now() + CACHE_TTL_MS,
      });
      if (shouldUseSessionCache) {
        writeSessionCache(cacheKey, data);
      }
      pendingRequests.delete(cacheKey);
      return data;
    })
    .catch((error) => {
      responseCache.delete(cacheKey);
      pendingRequests.delete(cacheKey);
      handleRequestError(error);
    });

  pendingRequests.set(cacheKey, request);
  return request;
}

export async function importarArquivo(arquivo) {
  const formData = new FormData();
  formData.append("arquivo", arquivo);

  const { data } = await axios
    .post(endpoint("/importacao/mapa"), formData, {
      headers: authMultipartHeaders(),
    })
    .catch(handleRequestError);

  clearContabilidadeApiCache();
  return data;
}

export async function listarCentrosCustoSienge({ forceRefresh = false } = {}) {
  if (forceRefresh) {
    clearContabilidadeApiCache("sienge-cost-centers");
  }

  return cachedGet("sienge-cost-centers", "/importacao/sienge/cost-centers", {}, authHeaders());
}

export async function importarViaSienge(payload) {
  const { data } = await axios
    .post(endpoint("/importacao/sienge/mapa"), payload, {
      headers: authHeaders(),
    })
    .catch(handleRequestError);

  clearContabilidadeApiCache();
  return data;
}

export async function listarImportacoes() {
  return cachedGet("importacao", "/importacao");
}

export async function listarEmpreendimentos() {
  return cachedGet("empreendimentos", "/empreendimentos");
}

export async function obterVendas(params = {}) {
  return cachedGet("vendas", "/vendas", params);
}

export async function obterCarteiraRecebiveis(params = {}) {
  return cachedGet("carteira-recebiveis", "/carteira-recebiveis", params);
}

export async function obterCustos(params = {}) {
  return cachedGet("custos", "/custos", params);
}

export async function obterImpostos(params = {}) {
  return cachedGet("impostos", "/impostos", params);
}

export async function obterDespesas(params = {}) {
  return cachedGet("despesas", "/despesas", params);
}

export async function obterVisaoGeral(params = {}) {
  return cachedGet("visao-geral", "/visao-geral", params);
}

export async function obterDreViabilidade(params = {}) {
  return cachedGet("dre-viabilidade", "/dre-viabilidade", params);
}

export async function listarDreViabilidadeVersoes(params = {}) {
  return cachedGet("dre-viabilidade-versoes", "/dre-viabilidade/simulacoes", params, authHeaders());
}

export async function obterDreViabilidadeVersao(id) {
  return cachedGet(`dre-viabilidade-versao-${id}`, `/dre-viabilidade/simulacoes/${id}`, {}, authHeaders());
}

export async function criarDreViabilidadeVersao(payload) {
  const { data } = await axios
    .post(endpoint("/dre-viabilidade/simulacoes"), payload, {
      headers: authHeaders(),
    })
    .catch(handleRequestError);
  clearContabilidadeApiCache(["dre-viabilidade-versoes"]);
  return data;
}

export async function atualizarDreViabilidadeVersao(id, payload) {
  const { data } = await axios
    .put(endpoint(`/dre-viabilidade/simulacoes/${id}`), payload, {
      headers: authHeaders(),
    })
    .catch(handleRequestError);
  clearContabilidadeApiCache(["dre-viabilidade-versoes", `dre-viabilidade-versao-${id}`]);
  return data;
}

export async function excluirDreViabilidadeVersao(id) {
  await axios
    .delete(endpoint(`/dre-viabilidade/simulacoes/${id}`), {
      headers: authHeaders(),
    })
    .catch(handleRequestError);
  clearContabilidadeApiCache(["dre-viabilidade-versoes", `dre-viabilidade-versao-${id}`]);
}

export async function obterConfig(params = {}) {
  return cachedGet("config", "/config", params, authHeaders());
}

export async function salvarConfig(payload) {
  const { data } = await axios
    .put(endpoint("/config"), payload, {
      headers: authHeaders(),
    })
    .catch(handleRequestError);
  clearContabilidadeApiCache(["config", "impostos", "despesas", "dre-viabilidade"]);
  return data;
}

export async function perguntarAgente(payload) {
  const { data } = await axios
    .post(endpoint("/agente"), payload, {
      headers: authHeaders(),
    })
    .catch(handleRequestError);

  return data;
}
